/**
 * Content Performance (#87-#93) — the imported-analytics sidecar.
 *
 * Its own resource module because the feature is its own subsystem: no filter vocabulary is shared
 * with `/view` (`from`/`to`/`q` are the whole of it), the import is the app's only multipart
 * request, and nothing here is ever invalidated by an SSE event — the two mutations below drop the
 * `['content']` root instead.
 */

import type {
  ContentCommentsQuery,
  ContentCommentsResponse,
  ContentExportQuery,
  ContentImportResponse,
  ContentMetricsQuery,
  ContentMetricsResponse,
  ContentPostsQuery,
  ContentPostsResponse,
  ContentSessionsResponse,
} from '@lw/shared';
import { apiJson, apiUpload, apiUrl, apiVoid, type QueryParams } from './client';

/* ------------------------------------------------------------------ metrics */

/** #87 `GET /api/content/metrics` — the three chart sections plus `meta`. */
export function getContentMetrics(
  params: Partial<ContentMetricsQuery> = {},
  signal?: AbortSignal,
): Promise<ContentMetricsResponse> {
  return apiJson<ContentMetricsResponse>('/content/metrics', {
    query: { from: params.from, to: params.to, granularity: params.granularity },
    ...(signal ? { signal } : {}),
  });
}

/* ------------------------------------------------------------------- tables */

function tableQuery(params: Partial<ContentPostsQuery | ContentCommentsQuery>): QueryParams {
  return {
    from: params.from,
    to: params.to,
    q: params.q,
    offset: params.offset,
    limit: params.limit,
    sort: params.sort,
    dir: params.dir,
  };
}

/** #88 `GET /api/content/posts` — one offset window of the merged posts table. */
export function getContentPosts(
  params: Partial<ContentPostsQuery> = {},
  signal?: AbortSignal,
): Promise<ContentPostsResponse> {
  return apiJson<ContentPostsResponse>('/content/posts', {
    query: tableQuery(params),
    ...(signal ? { signal } : {}),
  });
}

/** #89 `GET /api/content/comments` — same contract; own-post comments never appear. */
export function getContentComments(
  params: Partial<ContentCommentsQuery> = {},
  signal?: AbortSignal,
): Promise<ContentCommentsResponse> {
  return apiJson<ContentCommentsResponse>('/content/comments', {
    query: tableQuery(params),
    ...(signal ? { signal } : {}),
  });
}

/* ------------------------------------------------------------------ import */

/**
 * #90 `POST /api/content/import` — the one multipart request in the app.
 *
 * `confirm` is the handle-clash override: the first attempt goes without it, a `409` carrying
 * `ContentHandleMismatchDetails` is answered by the modal, and the approved retry sends `confirm=1`
 * with the same file. Nothing is persisted by the refused attempt, so the retry is a plain re-post.
 */
export function importContentArchive(
  file: File,
  options: { confirm?: boolean } = {},
): Promise<ContentImportResponse> {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiUpload<ContentImportResponse>('/content/import', form, {
    query: options.confirm === true ? { confirm: true } : {},
  });
}

/* ---------------------------------------------------------------- sessions */

/** #91 `GET /api/content/sessions` — newest import first. */
export function getContentSessions(signal?: AbortSignal): Promise<ContentSessionsResponse> {
  return apiJson<ContentSessionsResponse>('/content/sessions', { ...(signal ? { signal } : {}) });
}

/** #92 `DELETE /api/content/sessions/:id` — cascades its rows and unlinks the stored ZIP. */
export function deleteContentSession(id: number): Promise<void> {
  return apiVoid(`/content/sessions/${String(id)}`, { method: 'DELETE' });
}

/* ------------------------------------------------------------------ export */

/**
 * #93 `GET /api/content/export` — a streamed file, so this returns the URL for an `<a download>`
 * rather than fetching it (the #29 convention).
 */
export function contentExportUrl(params: Partial<ContentExportQuery> = {}): string {
  return apiUrl('/content/export', {
    format: params.format,
    table: params.table,
    columns: params.columns,
    sort: params.sort,
    dir: params.dir,
    q: params.q,
    from: params.from,
    to: params.to,
  });
}
